// @flow

import styled from '@emotion/styled'
import { Link } from 'gatsby'
import { GatsbyImage, StaticImage } from 'gatsby-plugin-image'
import * as React from 'react'

import { colors, media } from '@toggl/style'
import { getAccountsUrl } from '@toggl/toggl/src/utils'
import { Button, Responsive, text } from '@toggl/ui'

declare var process: WebProcess

type Props = {|
  className?: string,
  quote: {
    text: string,
    description: string,
    link?: string,
    image:
      | React.Element<typeof GatsbyImage>
      | React.Element<typeof StaticImage>
      | React.Element<'img'>,
  },
  persuader: {
    text: string,
    cta?: string,
    link?: string,
  },
  logo?: {
    image:
      | React.Element<typeof GatsbyImage>
      | React.Element<typeof StaticImage>
      | React.Element<'img'>,
    mobileImage:
      | React.Element<typeof GatsbyImage>
      | React.Element<typeof StaticImage>
      | React.Element<'img'>,
  },
|}

export default function QuoteLogos({
  className,
  quote,
  persuader,
  logo,
}: Props) {
  return (
    <Root className={className}>
      <Wrapper>
        <QuoteStyled>
          <TextSection>
            <text.H4 color={colors.darkPurple100}>{quote.text}</text.H4>
            <text.P3 color={colors.darkPurple90}>
              &#x2014; {quote.description}
            </text.P3>

            {quote.link && (
              <Button.Secondary
                as={Link}
                to={quote.link}
                color={colors.darkPurple100}
                caretDirection='right'
                data-heap-id='pw-case-study-click'
              >
                Read the case study
              </Button.Secondary>
            )}
            <Responsive.DesktopOnly>{quote.image}</Responsive.DesktopOnly>
          </TextSection>

          <CTA>
            <text.H4 color={colors.darkPurple100}>{persuader.text}</text.H4>

            {persuader.link ? (
              persuader.link === '/track/signup/' ? (
                <Button.Primary
                  as='a'
                  href={new URL(persuader.link, getAccountsUrl())}
                  data-heap-id='pw-case-study-signup'
                >
                  {persuader.cta}
                </Button.Primary>
              ) : (
                <Button.Primary
                  as={Link}
                  to={persuader.link}
                  data-heap-id='pw-case-study-signup'
                >
                  {persuader.cta}
                </Button.Primary>
              )
            ) : (
              <Button.Primary
                as='a'
                href={new URL('/track/signup/', getAccountsUrl())}
                data-heap-id='pw-case-study-signup'
              >
                Try Toggl Track
              </Button.Primary>
            )}
          </CTA>
        </QuoteStyled>

        {logo && (
          <LogoWrapper>
            <Responsive.DesktopOnly>{logo.image}</Responsive.DesktopOnly>
            <Responsive.MobileOnly>{logo.mobileImage}</Responsive.MobileOnly>
          </LogoWrapper>
        )}
      </Wrapper>
    </Root>
  )
}

const QuoteStyled: React.ComponentType<any> = styled.div`
  background: ${colors.peach80};
  padding: 30px;
  display: flex;
  flex-wrap: wrap;
  column-gap: 40px;
  ${media.mq[1]} {
    border-radius: 10px;
    padding: 40px 45px 30px;
  }
`

const TextSection = styled.div`
  position: relative;
  ${media.mq[1]} {
    padding-right: 250px;
  }
  p {
    margin: 20px 10px 0 0;
    display: inline-block;
  }
  a {
    display: inline;
  }
  .gatsby-image-wrapper {
    position: absolute;
    top: -60px;
    right: 0;
    max-width: 200px;
  }
`

const Root: React.ComponentType<any> = styled.div`
  padding: 40px 0;
  ${media.mq[2]} {
    padding: 80px 0;
  }
`

const Wrapper = styled.div`
  max-width: 1140px;
  margin: auto;
  position: relative;
  ${media.mq[2]} {
    &:before,
    &:after {
      position: absolute;
      height: 40px;
    }
    &:after {
      content: url(${require('./assets/shape-eclipse.svg').default});
      right: -5%;
      bottom: 30%;
    }
    &:before {
      content: url(${require('./assets/shape-multi.svg').default});
      left: -90px;
      top: -80px;
    }
  }
`

const CTA = styled.div`
  border-top: 2px solid ${colors.pink80};
  width: 100%;
  margin-top: 30px;
  padding: 25px 0 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 25px;
  ${media.mq[1]} {
    flex-direction: row;
    justify-content: space-between;
  }
`

const LogoWrapper = styled.div`
  text-align: center;
  margin: 45px auto 0;
`
